import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';

// The design's root wrapper is `.we` with a `data-theme` of dark (default) | light | system.
// tokens.css defines all three theme blocks against that selector. This provider owns the value
// and persists the operator's choice.
export type Theme = 'dark' | 'light' | 'system';

const STORAGE_KEY = 'xlooop.theme';

interface ThemeCtx {
  theme: Theme;
  setTheme: (t: Theme) => void;
  cycleTheme: () => void;
}

const Ctx = createContext<ThemeCtx | null>(null);

function readInitialTheme(): Theme {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored === 'dark' || stored === 'light' || stored === 'system') return stored;
  } catch {
    /* localStorage unavailable — fall through to default */
  }
  return 'dark';
}

export function ThemeRoot({ children }: { children: React.ReactNode }): React.ReactElement {
  const [theme, setThemeState] = useState<Theme>(readInitialTheme);

  const setTheme = (t: Theme) => {
    setThemeState(t);
    try {
      localStorage.setItem(STORAGE_KEY, t);
    } catch {
      /* ignore persistence failure */
    }
  };

  // Honor prefers-reduced-motion at the root so tokens.css motion rules gate correctly.
  useEffect(() => {
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    const apply = () => document.documentElement.setAttribute('data-reduce-motion', mq.matches ? '1' : '0');
    apply();
    mq.addEventListener('change', apply);
    return () => mq.removeEventListener('change', apply);
  }, []);

  const value = useMemo<ThemeCtx>(
    () => ({
      theme,
      setTheme,
      cycleTheme: () => setTheme(theme === 'dark' ? 'light' : theme === 'light' ? 'system' : 'dark'),
    }),
    [theme],
  );

  return (
    <Ctx.Provider value={value}>
      <div className="we" data-theme={theme} style={{ minHeight: '100vh' }}>
        {children}
      </div>
    </Ctx.Provider>
  );
}

export function useTheme(): ThemeCtx {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error('useTheme must be used within ThemeRoot');
  return ctx;
}
